import { forwardRef, ReactNode } from "react";
import cx from "classnames";
import { MenuItem, MenuItemProps } from "react-aria-components";

import Tooltip from "ds/components/Tooltip";
import { TooltipProps } from "ds/components/Tooltip/types";
import { AnalyticsCommonProps } from "hooks/useAnalytics";
import { Spinner } from "components/Spinner";
import useAriaHrefProps from "components/AriaRouterProvider/useAriaHrefProps";
import Icon from "ds/components/Icon";

import styles from "./styles.module.css";
import useAnalyticsAction from "./useAnalyticsAction";

export type DropdownMenuItemProps = MenuItemProps &
  AnalyticsCommonProps & {
    active?: boolean;
    danger?: boolean;
    loading?: boolean;
    tooltipPlacement?: TooltipProps["placement"];
    tooltipWidthMode?: TooltipProps["widthMode"];
    tooltipDelay?: TooltipProps["delay"];
    tooltipDisableAriaLabel?: TooltipProps["disableAriaLabel"];
    isTooltipActive?: boolean;
    tooltip?: ReactNode;
    activeClassName?: string;
  };

const DropdownMenuItem = forwardRef<HTMLDivElement, DropdownMenuItemProps>(
  (
    {
      children,
      danger,
      loading,
      isDisabled,
      className,
      active,
      onAction,
      tooltip,
      isTooltipActive = true,
      tooltipPlacement,
      tooltipWidthMode,
      tooltipDelay,
      tooltipDisableAriaLabel,
      analyticsPage,
      analyticsTitle,
      analyticsProps,
      href,
      activeClassName = "",
      ...restProps
    }: DropdownMenuItemProps,
    ref
  ) => {
    const classNames = cx(
      styles.item,
      {
        [styles.danger]: danger,
        [styles.active]: active && !activeClassName,
        [activeClassName]: !!activeClassName && active,
      },
      className
    );
    const handleAction = useAnalyticsAction({
      analyticsPage,
      analyticsTitle,
      analyticsProps,
      onAction,
    });
    const hrefProps = useAriaHrefProps(href);

    if (tooltip && isTooltipActive) {
      return (
        <Tooltip
          delay={tooltipDelay}
          placement={tooltipPlacement}
          widthMode={tooltipWidthMode}
          disableAriaLabel={tooltipDisableAriaLabel}
          on={(tooltipProps) => (
            <MenuItem
              className={classNames}
              isDisabled={isDisabled}
              onAction={handleAction}
              {...restProps}
              {...tooltipProps}
              {...hrefProps}
              ref={tooltipProps.ref}
            >
              {children}
            </MenuItem>
          )}
        >
          {tooltip}
        </Tooltip>
      );
    }

    return (
      <MenuItem
        className={classNames}
        isDisabled={isDisabled}
        onAction={handleAction}
        {...restProps}
        {...hrefProps}
        ref={ref}
      >
        <>
          {loading ? (
            <>
              <Icon src={Spinner} size="stretch" className={styles.spinner} />
              {typeof children === "string" && (
                <span className={styles.loadingText}>{children} is loading</span>
              )}
            </>
          ) : (
            children
          )}
        </>
      </MenuItem>
    );
  }
);

DropdownMenuItem.displayName = "DS.DropdownMenu.Item";

export default DropdownMenuItem;
